/**
 * Utilidades para calcular correctamente el IVA en El Salvador (13%)
 * 
 * MÉTODO CORRECTO PARA AMORTIZACIONES:
 * - El monto de la amortización es la BASE IMPONIBLE (precio sin IVA)
 * - IVA = baseImponible * 0.13 (truncado a 2 decimales, NO redondeado)
 * - Total = baseImponible + IVA
 */
const IVA_RATE = 0.13; // 13%
export interface IVACalculation {
  /** Monto total con IVA incluido */
  totalConIVA: number;
  /** Base imponible (precio sin IVA) */
  baseImponible: number;
  /** Monto del IVA */
  iva: number;
  /** Verificación: baseImponible + iva debe ser igual a totalConIVA */
  verificacion: {
    suma: number;
    diferencia: number;
    esCorrecta: boolean;
  };
}
/**
 * Trunca un número a 2 decimales SIN redondear
 * @param value - Valor a truncar
 * @returns Valor truncado a 2 decimales
 */
const truncarA2Decimales = (value: number): number => {
  return Math.trunc(value * 100) / 100;
};
/**
 * Calcula el IVA directamente como total * 0.13 y trunca a 2 decimales
 * @param totalConIVA - Monto total que incluye IVA
 * @returns Objeto con cálculos de IVA
 */
export function calcularIVADesdeTotal(totalConIVA: number): IVACalculation {
  // Redondear el input a 2 decimales para evitar problemas de floating point
  const total = Math.round(totalConIVA * 100) / 100;
  // MÉTODO CORRECTO: Calcular IVA como total * 0.13 y truncar a 2 decimales
  const ivaCalculadoBruto = total * IVA_RATE;
  const iva = truncarA2Decimales(ivaCalculadoBruto);
  // Calcular base imponible restando el IVA del total
  const baseImponible = total - iva;
  // Verificación
  const suma = baseImponible + iva;
  const diferencia = Math.abs(total - suma);
  const esCorrecta = diferencia < 0.01; // Tolerancia de 1 centavo
  return {
    totalConIVA: total,
    baseImponible: Math.round(baseImponible * 100) / 100, // Redondear base imponible
    iva,
    verificacion: {
      suma: Math.round(suma * 100) / 100,
      diferencia,
      esCorrecta
    }
  };
}
/**
 * Calcula el total con IVA desde una base imponible (precio sin IVA)
 * ESTE ES EL MÉTODO CORRECTO PARA AMORTIZACIONES
 * @param baseImponible - Precio sin IVA (monto de la amortización)
 * @returns Objeto con cálculos de IVA
 */
export function calcularIVADesdeBase(baseImponible: number): IVACalculation {
  // Redondear el input a 2 decimales
  const base = Math.round(baseImponible * 100) / 100;
  // Calcular IVA usando truncamiento en lugar de redondeo
  const ivaCalculado = base * IVA_RATE;
  const iva = truncarA2Decimales(ivaCalculado);
  // Calcular total
  const totalConIVA = base + iva;
  return {
    totalConIVA,
    baseImponible: base,
    iva,
    verificacion: {
      suma: totalConIVA,
      diferencia: 0,
      esCorrecta: true
    }
  };
}
/**
 * Función de conveniencia: devuelve solo el IVA desde un total con IVA
 * @param totalConIVA - Monto total que incluye IVA
 * @returns Monto del IVA
 */
export function obtenerIVADesdeTotal(totalConIVA: number): number {
  return calcularIVADesdeTotal(totalConIVA).iva;
}
/**
 * Función de conveniencia: devuelve solo la base imponible desde un total con IVA
 * @param totalConIVA - Monto total que incluye IVA
 * @returns Base imponible (precio sin IVA)
 */
export function obtenerBaseDesdeTotal(totalConIVA: number): number {
  return calcularIVADesdeTotal(totalConIVA).baseImponible;
}
/**
 * Valida un cálculo de IVA y devuelve un mensaje de error si es incorrecto
 * @param totalConIVA - Total con IVA
 * @param baseDeclarada - Base imponible declarada
 * @param ivaDeclarado - IVA declarado
 * @returns null si es correcto, string con error si es incorrecto
 */
export function validarCalculoIVA(
  totalConIVA: number, 
  baseDeclarada: number, 
  ivaDeclarado: number
): string | null {
  const calculoCorrecto = calcularIVADesdeTotal(totalConIVA);
  const diferenciaBase = Math.abs(calculoCorrecto.baseImponible - baseDeclarada);
  const diferenciaIVA = Math.abs(calculoCorrecto.iva - ivaDeclarado);
  if (diferenciaBase > 0.01) {
    return `Base imponible incorrecta. Esperado: ${calculoCorrecto.baseImponible}, Recibido: ${baseDeclarada}`;
  }
  if (diferenciaIVA > 0.01) {
    return `IVA incorrecto. Esperado: ${calculoCorrecto.iva}, Recibido: ${ivaDeclarado}`;
  }
  return null;
}

